import { Injectable } from '@angular/core';
import { Product, ProductProperty, ProductResponse } from '../models/products.types';
import { PropertyValueWithUnit } from '../../../core/models/abacus-api.types';
import {
  ArrowDownLeft,
  ArrowUpRight,
  BadgeDollarSign,
  BatteryCharging,
  CircleGauge,
  EvCharger,
  Fuel,
  Gauge,
  Layers,
  LucideIconData,
  Plug,
  Repeat2,
  Snowflake,
  Tag,
  TrendingUp,
  WindArrowDown,
  Zap,
} from 'lucide-angular';

export interface PropertyConfig {
  label: string;
  icon?: LucideIconData;
}

@Injectable({ providedIn: 'root' })
export class ProductMapper {
  private readonly propertyRegistry = new Map<keyof ProductResponse, PropertyConfig>([
    // Common / pricing
    ['type', { label: 'Product type', icon: Tag }],

    // Generator
    ['PSC_Stage_Name__c', { label: 'Generator stage', icon: Layers }],
    ['KW_rating__c', { label: 'Power', icon: Zap }],
    ['energy_generated', { label: 'Energy generated', icon: BatteryCharging }],
    ['Fuel_Type__c', { label: 'Fuel type', icon: Fuel }],
    ['Fuel_consumption_100__c', { label: 'Fuel consumption', icon: Gauge }],

    // Battery
    ['nominal_capacity', { label: 'Nominal capacity', icon: Layers }],
    ['Rated_Net_Discharge_power__c', { label: 'Max power out', icon: ArrowUpRight }],
    ['Rated_Net_Recharge_power__c', { label: 'Max power in', icon: ArrowDownLeft }],

    // Compressor
    ['working_pressure', { label: 'Working pressure', icon: WindArrowDown }],
    ['fad_at_working_pressure', { label: 'FAD at working pressure', icon: Gauge }],
    ['Max_Pressure__c', { label: 'Max pressure', icon: TrendingUp }],
    ['compressor_stage', { label: 'Stage', icon: Layers }],
    ['compressor_efficiency', { label: 'Compressor efficiency', icon: CircleGauge }],
    ['specific_energy_requirement', { label: 'Specific energy requirement', icon: CircleGauge }],
    ['energy_cost', { label: 'Energy cost', icon: BadgeDollarSign }],

    // Dryer
    ['PSC_PDP__c', { label: 'Pressure dew point', icon: Snowflake }],
    ['purge_losses', { label: 'Regeneration losses', icon: Repeat2 }],
    ['energy_consumption', { label: 'Energy consumption', icon: EvCharger }],

    // Boiler
    ['PSC_Maximum_NetPower__c', { label: 'Maximum load', icon: Gauge }],
    ['PSC_Maximum_NetPower_Economizer__c', { label: 'Maximum load with economizer', icon: TrendingUp }],
    ['PSC_Fuel_Type_2__c', { label: 'Energy source', icon: Plug }],
  ]);

  mapToProduct(response: ProductResponse): Product {
    return {
      id: response.Id,
      parentSolutionId: response.PSC_Parent_Solution__c,
      type: response.type,
      name: response.PSC_Product_Name__c ?? 'Unknown Product',
      quantity: response.PSC_Quantity__c ?? 1,
      fuelType: response.Fuel_Type__c,
      properties: this.extractProperties(response),
    };
  }

  mapToProducts(responses: ProductResponse[]): Product[] {
    return responses.map(r => this.mapToProduct(r));
  }

  private extractProperties(response: ProductResponse): ProductProperty[] {
    const properties: ProductProperty[] = [];

    for (const [key, config] of this.propertyRegistry) {
      const rawValue = response[key];
      const property = this.normalizeValue(rawValue, config);
      if (property) {
        properties.push(property);
      }
    }

    return properties;
  }

  private normalizeValue(value: unknown, config: PropertyConfig): ProductProperty | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (this.isPropertyValueWithUnit(value)) {
      if (value.value === null) {
        return null;
      }
      return {
        label: config.label,
        value: value.value,
        unit: value.unit ?? undefined,
        icon: config.icon,
      };
    }

    if (typeof value === 'string') {
      return {
        label: config.label,
        value: value.replace(/_/g, ' '),
        icon: config.icon,
      };
    }

    if (typeof value === 'number') {
      return {
        label: config.label,
        value,
        icon: config.icon,
      };
    }

    return null;
  }

  private isPropertyValueWithUnit(value: unknown): value is PropertyValueWithUnit<number | null, string | null> {
    return typeof value === 'object' && value !== null && 'value' in value && 'unit' in value;
  }
}
